import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, switchMap, take, catchError, throwError, retry, filter } from 'rxjs';
import { ClerkService } from 'ngx-clerk-iliad';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';

// Helper function to decode JWT tokens
function decodeJwt(token: string): { header: any, payload: any } {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[JWT Decoder] Invalid token format');
      return { header: null, payload: null };
    }

    // Decode header (first part)
    const headerBase64 = parts[0].replace(/-/g, '+').replace(/_/g, '/');
    const headerJson = atob(headerBase64);
    const header = JSON.parse(headerJson);

    // Decode payload (second part)
    const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payloadJson = atob(payloadBase64);
    const payload = JSON.parse(payloadJson);

    return { header, payload };
  } catch (error) {
    console.error('[JWT Decoder] Error decoding token:', error);
    return { header: null, payload: null };
  }
}

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const clerkService = inject(ClerkService);
  const authService = inject(AuthService);


  // Only intercept requests to our API
  if (!request.url.startsWith(environment.apiUrl)) {
    return next(request);
  }

  // Get the Clerk instance and add the session token to the request
  return clerkService.clerk$.pipe(
    // Wait until Clerk is initialized and a session exists
    // This avoids racing the first emission (which can be undefined) and sending unauthenticated API calls
    // Filter ensures we only proceed when both the Clerk instance and its session are present
    // Then take the first valid emission
    filter((clerk: any) => !!clerk && !!clerk.session),
    take(1),
    switchMap(clerk => {
      // Get the session token

      // Get the standard session token without template parameter
      // The backend expects a JWT that can be validated against the Clerk JWKS
      return from(
        // Use the standard getToken method without parameters
        (clerk.session.getToken()
          .catch((error: any) => {
            return Promise.resolve<string | null>(null);
          }) as Promise<string | null>)
      ).pipe(
        switchMap((token: string | null) => {
          if (!token) {
            return next(request);
          }

          // Clone the request and add the authorization header
          const authReq = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          });

          // Pass the cloned request with the token to the next handler
          return next(authReq).pipe(
            // Add retry logic for server errors (500) and auth errors (401)
            // This will retry the request once after a brief delay
            retry({
              count: 1,
              delay: (error, retryCount) => {
                if (error instanceof HttpErrorResponse) {
                  const status = error.status;
                  if (status === 401 || status === 500) {
                    // Wait 1 second before retrying
                    return new Observable(subscriber => {
                      setTimeout(() => {
                        // Instead of just completing, we need to emit a value
                        // to prevent EmptyError when using firstValueFrom
                        subscriber.next(true); // Emit a value (true) to signal retry
                        subscriber.complete();
                      }, 1000);
                    });
                  }
                }
                // For other errors, don't retry
                return throwError(() => error);
              }
            }),
            // Catch and log HTTP errors
            catchError((error: HttpErrorResponse) => {
              console.error(`[Auth Interceptor] Request failed with status ${error.status}:`, error.message);
              if (error.error instanceof Object) {
                console.error('[Auth Interceptor] Error details:', error.error);
              }

              // If the request fails with 401 (Unauthorized) or 403 (Forbidden)
              // it means the session is invalid, expired, or compromised.
              // To prevent the "stuck" state, we force a logout.
              if (error.status === 401 || error.status === 403) {
                console.warn('[Auth Interceptor] Authentication failure detected. Force logging out...');
                authService.logout();
              }

              // Re-throw the error to be handled by the application
              return throwError(() => error);
            })
          );
        })
      );
    }),
    catchError((error: any) => {
      console.error('[Auth Interceptor] Error in auth interceptor:', error);
      // If there's an error getting the token, proceed with the original request
      return next(request);
    })
  );
};
